import { useEffect, useMemo } from "react";
import { SkeletonLoading } from "react-migration/components/SkeletonLoading";
import { Insight } from "react-migration/components/Insight/Insight";
import { useSelectionSidebarLoading } from "../../../Bundle/useSelectionSidebarLoadingState";
import { LayerTypeLayerSummaryProps, SupportedRegions } from "../../../types";
import { useGreenGreyBeltInsights as useGreyBeltInsights } from "./useGreenGreyBeltKeyInsights";
import { getBeltClassifications } from "./getBeltClassifications";
import { Belts, InsightStatus } from "./types";
import { GREY_BELT_CATEGORIES } from "./constants";
import { Tooltip } from "react-migration/components/Tooltip";
import { Theme } from "react-migration/lib/theme/Theme";
import { useDesignationWithLargestOverlapByGeometry } from "react-migration/domains/constraints/designation/hooks/useDesignationWithLargestOverlapByGeometry";
import { InsightStatusType } from "react-migration/components/Insight/components/Body/Body";
import { Alert } from "react-migration/components/Alert";

function getUnmetNeedStatus(greybeltDesignationInfo: Belts) {
  const designation = greybeltDesignationInfo?.designation;
  const presumptionInFavor = designation?.designation_attributes?.find((item) => item.key === "presumption_in_favour");

  if (!presumptionInFavor) {
    return {
      title: "Presumption in favour of sustainable development",
      value: "N/A",
    };
  }

  return {
    title: "Presumption in favour of sustainable development",
    value: presumptionInFavor.value,
  };
}

const defaultNPPFStatuses = [
  {
    title: "Purpose A",
    value: "N/A",
    color: "grey" as InsightStatusType,
  },
  {
    title: "Purpose B",
    value: "N/A",
    color: "grey" as InsightStatusType,
  },
  {
    title: "Purpose D",
    value: "N/A",
    color: "grey" as InsightStatusType,
  },
];

const defaultSustainabilityStatuses = [
  {
    title: "Connectivity Score",
    value: "N/A",
    color: "grey" as InsightStatusType,
  },
  {
    title: "Coverage by BMV",
    value: "N/A",
    color: "grey" as InsightStatusType,
  },
];

const purposeColor = {
  Na: "grey",
  Strong: "red",
  Moderate: "amber",
  Weak: "green",
} as unknown as Record<string, InsightStatusType>;

function getSustainabilityStatus(greybeltDesignationInfo: Belts) {
  const designation = greybeltDesignationInfo?.designation;

  const connectivityScoreAttribute = designation?.designation_attributes?.find(
    (item) => item.key === "site_connectivity_score"
  );

  const bmvCoverage = designation?.designation_attributes?.find((item) => item.key === "bmv_coverage");

  if (!bmvCoverage || !connectivityScoreAttribute) {
    return defaultSustainabilityStatuses;
  }

  const bmvCoverageValue = Math.round(parseFloat(bmvCoverage?.value ?? "0"));
  const bmv = {
    title: "Coverage by BMV",
    value: bmvCoverage?.value === null ? "N/A" : `${bmvCoverageValue}%`,
    color: (bmvCoverageValue >= 95 ? "red" : "green") as InsightStatusType,
  };

  const connectivityScoreValue = Math.round(parseInt(connectivityScoreAttribute?.value ?? "0"));

  const connectivityScore = {
    title: "Connectivity Score",
    value: connectivityScoreAttribute.value === null ? "N/A" : connectivityScoreValue,
    color: "grey" as InsightStatusType,
  };

  if (connectivityScoreAttribute.value === null) {
    connectivityScore.color = "grey";
  } else if (connectivityScoreValue >= 7) {
    connectivityScore.color = "green";
  } else if (connectivityScoreValue == 6) {
    connectivityScore.color = "amber";
  } else {
    connectivityScore.color = "red";
  }

  return [connectivityScore, bmv];
}

function getNPPFStatuses(greybeltDesignationInfo: Belts): InsightStatus[] {
  const designation = greybeltDesignationInfo?.designation;

  const purposeA = designation?.designation_attributes?.find((item) => item.key === "purpose_a");
  const purposeB = designation?.designation_attributes?.find((item) => item.key === "purpose_b");
  const purposeD = designation?.designation_attributes?.find((item) => item.key === "purpose_d");

  if (!purposeA || !purposeB || !purposeD) {
    return defaultNPPFStatuses;
  }

  const purposeAColor = purposeColor[purposeA.value as keyof typeof purposeColor];
  const purposeBColor = purposeColor[purposeB.value as keyof typeof purposeColor];
  const purposeDColor = purposeColor[purposeD.value as keyof typeof purposeColor];

  return [
    {
      title: "Purpose A",
      value: purposeA.value ?? "N/A",
      tooltip: "To check the unrestricted sprawl of large built-up areas.",
      color: purposeAColor,
    },
    {
      title: "Purpose B",
      value: purposeB.value ?? "N/A",
      tooltip: "To prevent neighbouring towns from merging into one another.",
      color: purposeBColor,
    },
    {
      title: "Purpose D",
      value: purposeD.value ?? "N/A",
      tooltip: "To preserve the setting and special character of historic towns.",
      color: purposeDColor,
    },
  ];
}

export function GreyBeltLayerSummary({ selection, onLoadingChange, print }: LayerTypeLayerSummaryProps) {
  const geometry = selection.feature?.geometry ?? null;

  const rootCategories = GREY_BELT_CATEGORIES;

  const { designation, designations, loading, error } = useDesignationWithLargestOverlapByGeometry({
    geometry,
    rootCategories,
    bufferMeters: -1,
    includeArea: true,
    sourceStatuses: ["adopted"],
    attributeKeys: [
      "overall_favourability",
      "purpose_a",
      "purpose_b",
      "purpose_d",
      "bmv_coverage",
      "site_connectivity_score",
      "presumption_in_favour",
    ],
  });

  const belts = useMemo(() => getBeltClassifications(designation), [designation]);
  useSelectionSidebarLoading(onLoadingChange, loading);
  useGreyBeltInsights(belts);

  useEffect(() => {
    onLoadingChange(loading);
  }, [onLoadingChange, loading]);

  if (loading) {
    return (
      <div className="relative p-2 h-[168px]">
        <SkeletonLoading rows={2} showPadding={false} bgClassName="bg-transparent" />
      </div>
    );
  }

  if (!belts?.designation) {
    return !print ? <Alert.Info>Selection is not impacted</Alert.Info> : null;
  }

  const greyBelt = getNPPFStatuses(belts);
  const sustainability = getSustainabilityStatus(belts);
  const unmetNeed = getUnmetNeedStatus(belts);

  if (error) {
    // caught by error boundary
    throw error;
  }

  return (
    <>
      <div className="flex flex-col gap-2">
        <div className="text-sm inline-flex max-w-full gap-x-1">
          <span className="text-text-base-primary cursor-default truncate" title="NPPF Purpose">
            NPPF Purpose
          </span>
          <Tooltip.Wrapper>
            <Tooltip.Content size="sm" side="top" align="center" theme={Theme.Light}>
              <Tooltip.Description>
                <p>
                  How the land contributes to the green belt purposes of A, B and D. We are unable to assess against C
                  and E. Weaker is better.
                </p>
              </Tooltip.Description>
            </Tooltip.Content>
            <Tooltip.Trigger>
              <i className="icon-lt-information-line text-text-base-secondary-a cursor-pointer print:hidden" />
            </Tooltip.Trigger>
          </Tooltip.Wrapper>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {greyBelt.map((belt) => (
            <Insight.Wrapper key={belt.title} title={belt.title} tooltip={belt.tooltip}>
              <Insight.Body status={belt.color}>{belt.value}</Insight.Body>
            </Insight.Wrapper>
          ))}
        </div>
        <div className="text-sm inline-flex max-w-full gap-x-1">
          <span className="text-text-base-primary cursor-default truncate" title="NPPF Purpose">
            Sustainability
          </span>
          <Tooltip.Wrapper>
            <Tooltip.Content size="sm" side="top" align="center" theme={Theme.Light}>
              <Tooltip.Description>
                Well located to public transport (trains, buses, etc), and that avoids an Agricultural Land
                Classification of Best and Most Versatile (BMV) land, will score well
              </Tooltip.Description>
            </Tooltip.Content>
            <Tooltip.Trigger>
              <i className="icon-lt-information-line text-text-base-secondary-a cursor-pointer print:hidden" />
            </Tooltip.Trigger>
          </Tooltip.Wrapper>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {sustainability.map((sustainabilityItem) => (
            <Insight.Wrapper key={sustainabilityItem.title} title={sustainabilityItem.title}>
              <Insight.Body status={sustainabilityItem.color}>{sustainabilityItem.value}</Insight.Body>
            </Insight.Wrapper>
          ))}
        </div>
        <div className="text-sm inline-flex max-w-full gap-x-1">
          <span className="text-text-base-primary cursor-default truncate" title="NPPF Purpose">
            Demonstrable unmet need
          </span>
          <Tooltip.Wrapper>
            <Tooltip.Content size="sm" side="top" align="center" theme={Theme.Light}>
              <Tooltip.Description>
                Reviews the planning authorities Housing Land Supply, their Housing Delivery Test Score, alongside the
                date of adoption for their most recent Local Plan
              </Tooltip.Description>
            </Tooltip.Content>
            <Tooltip.Trigger>
              <i className="icon-lt-information-line text-text-base-secondary-a cursor-pointer print:hidden" />
            </Tooltip.Trigger>
          </Tooltip.Wrapper>
        </div>
        <div className="grid grid-cols-1 gap-2">
          <Insight.Wrapper key={unmetNeed.title} title={unmetNeed.title}>
            <Insight.Body>{unmetNeed.value}</Insight.Body>
          </Insight.Wrapper>
        </div>

        {designations?.length > 1 && (
          <span className="text-xs text-neutral-400 my-2">
            This insight may not refer to the entire area of your selected site. Assessments are undertaken based on
            ownership parcels and the insight refers to the largest parcel that intersects your selected site.
          </span>
        )}
      </div>
      {/* TODO: add a proper learn more section */}
      {/* <ConnectivityScoreLearnMore /> */}
    </>
  );
}

GreyBeltLayerSummary.icon = "icon-lt-belt";
GreyBeltLayerSummary.supportedRegions = [SupportedRegions.england];
